import { IncomeTypesLogic } from "./IncomeTypesLogic";
import { CalculationRowType, CalculationRowUnit, Unit, Language, CompanyAccountSettings, CalculationRowSource, RowTypeDefinition, CalcRowConfig, CalculationRowCategory, CalcRowConfigNumberInput, CalcRowConfigTypeGroup} from "../model";
import { EnumerationsLogic } from "./EnumerationsLogic";
import { calcRowsMetadata } from "./model";
import { Translations } from "./Translations";

/**
 * Provides business logic for Calculation rows:
 * How to create user interfaces, reports etc. based on them and their categories.
 */
export class CalcRowsLogic {

  /**
   * Gets a single UI configuration for Calculation row (built-in row types only).
   * Searches through all row types without role filtering.
   * @param type Type for which the configuration is fetched.
   */
  public static getRowConfig(type: CalculationRowType): CalcRowConfig | undefined {
    if (!type) {
      return undefined;
    }
    if (type == CalculationRowType.Unknown) {
      return this.getUnknownConfig();
    }
    const result = CalcRowsLogic.getBuiltInRowConfigs().find((x) => x.name === type);
    return result;
  }

  /**
   * Gets an indicator string (1-2 characters) for a unit.
   * @param unit Unit that should be described.
   * @returns Given unit indicator or undefined if none is matched (convert to empty string if necessary).
   */
  public static getUnitIndicator(unit: CalculationRowUnit | Unit | string | undefined, language: Language | undefined = undefined): string | undefined {
    language = language || Translations.getLanguage() as any || Language.Fi;
    // TODO: Consider another location as this is more generic than just Calculations.
    switch (unit) {
      // TODO: Do views go-through and remove these non-standard strings.
      case "eur":
      case "euros":
      case "€":
        unit = Unit.Euro;
        break;
      case "day":
        unit = Unit.Days;
        break;
      case "hour":
        unit = Unit.Hours;
        break;
      case "km":
        unit = Unit.Kilometers;
        break;
      case "%":
        unit = Unit.Percent;
        break;
      case "week":
        unit = Unit.Weeks;
        break;
      case "month":
        unit = CalculationRowUnit.Months;
        break;
    }
    if (!unit || unit == Unit.Undefined || unit == Unit.One) {
      return undefined;
    }
    if (unit == CalculationRowUnit.Months) {
      return Translations.get(`SALAXY.ENUM.CalculationRowUnit.${unit}.label`, undefined, language || undefined);
    }
    // CalculationRowUnit is the same value as Unit for all the above.
    return Translations.get(`SALAXY.ENUM.Unit.${unit}.shortIndicator`, undefined, language || undefined);
  }

  /** Gets the row configuration for row type "unknown". */
  public static getUnknownConfig(): CalcRowConfig | undefined {
    if (!this.unknownRowConfig) {
      this.unknownRowConfig = this.getRowConfigInternal({
        order: 0,
        name: "unknown",
        category: CalculationRowCategory.Other,
        color: "#ccc",
        amount: {
          default: 1,
          input: CalcRowConfigNumberInput.Hidden,
        },
        total: { input: CalcRowConfigNumberInput.Hidden },
        price: { input: CalcRowConfigNumberInput.Hidden },
        group: CalcRowConfigTypeGroup.BuiltIn,
      });
    }
    return this.unknownRowConfig;
  }

  /** Row configuration for unknown row type. */
  private static unknownRowConfig: CalcRowConfig | undefined;

  private role: "company" | "household";

  private settings: CompanyAccountSettings;

  /**
   * Creates a new CalcRowsLogic helper wichh adapts to role logic (company/household).
   * @param settings The company account settings that may contain company specific roles or just a role marker for "household".
   */
  constructor(settings: CompanyAccountSettings | "household" | "company" = "company") {
    this.role = settings === "household" ? "household" : "company";
    if (typeof settings === "object") {
      this.settings = settings;
    }
  }

  /**
   * Gets the custom row configs from company settings if provided in the constructor.
   */
  public getCustomRowConfigs(): CalcRowConfig[] {
    if (!(this.settings as CompanyAccountSettings)?.calc?.salaryDefaults) {
      return [];
    }
    return (this.settings as CompanyAccountSettings)?.calc?.salaryDefaults?.filter(x => x.source == CalculationRowSource.CustomRowtype)
      .map(x => (x as RowTypeDefinition).config as CalcRowConfig) ?? [];
  }

  /**
   * Gets the row types by category.
   * The instance methods filter types based on role.
   * @param category Category to fetch
   */
  public getRowTypesByCategory(category: CalculationRowCategory): CalculationRowType[] {
    return CalcRowsLogic.rowsByCategory[category] || [];
  }

  /**
   * Gets the row configurations by category(ies).
   * The instance methods filter types based on role.
   * @param categories List of categories to filter by.
   */
  public getRowConfigsByCategory(categories: CalculationRowCategory[]): CalcRowConfig[] {
    const types: CalculationRowType[] = [];
    for (const cat of categories || []) {
      types.push(...this.getRowTypesByCategory(cat));
    }
    return this.getRowConfigsByType(types);
  }

  /**
   * Gets the row configurations based on a list of row types.
   * The instance methods filter types based on role.
   * @param types List of types to filter by.
   */
  public getRowConfigsByType(types: CalculationRowType[]): CalcRowConfig[] {
    types = types || [];
    return this.getRowConfigs().filter((x) => types.indexOf(x.rowType as CalculationRowType) >= 0);
  }

  /**
   * Gets a single UI configuration for Calculation row.
   * This method filters for user role. Use static method to return any row configuration (also faster).
   * The instance methods filter types based on role.
   * @param type Type for which the configuration is fetched.
   */
  public getRowConfig(type: CalculationRowType): CalcRowConfig | undefined {
    if (!type) {
      return undefined;
    }
    if (type == CalculationRowType.Unknown) {
      return CalcRowsLogic.getUnknownConfig();
    }
    const result = this.getRowConfigs().find((x) => x.name === type);
    return result;
  }

  /**
   * Gets all the UI configuration objects for calculation rows.
   * Caches the data based on role and language.
   */
  public getRowConfigs(): CalcRowConfig[] {
    if (this.role === "household") {
      return CalcRowsLogic.getBuiltInRowConfigs().filter((x) =>
        x.name !== CalculationRowType.Compensation
        && x.name !== CalculationRowType.Board
        && x.name !== CalculationRowType.IrIncomeType
        && x.name !== CalculationRowType.OtherCompensation
        && x.name !== CalculationRowType.NonProfitOrg
      );
    } else {
      return this.getCustomRowConfigs()
        .concat(CalcRowsLogic.getBuiltInRowConfigs().filter((x) => x.name !== CalculationRowType.ChildCareSubsidy))
        .sort((a, b) => (((a.order ?? 0) * 100) + (a.group == CalcRowConfigTypeGroup.BuiltIn ? 1 : 0)) - (((b.order ?? 0) * 100) + (b.group == CalcRowConfigTypeGroup.BuiltIn ? 1 : 0)))
        ;
    }
  }

  /** Gets all the row configs cached for each language if language changes. */
  public static getBuiltInRowConfigs(): CalcRowConfig[] {
    const lang = Translations.getLanguage();
    if (!CalcRowsLogic.rowsCache[lang]) {
      CalcRowsLogic.rowsCache[lang] = calcRowsMetadata.map((x) => CalcRowsLogic.getRowConfigInternal(x));
    }
    return CalcRowsLogic.rowsCache[lang];
  }

  /** Raw classification of row types to primary categories. */
  public static rowsByCategory = {

    /** Base salary types: the ones that you define directly */
    salary: [
      CalculationRowType.Salary,
      CalculationRowType.HourlySalary,
      CalculationRowType.MonthlySalary,
      CalculationRowType.TimeRatePay,
      CalculationRowType.Compensation,
      CalculationRowType.IrIncomeType,
      CalculationRowType.Board,
      CalculationRowType.PaidSickLeave,
      CalculationRowType.PaidSickLeaveSalary,
      CalculationRowType.PaidSickLeaveHourlySalary,
      CalculationRowType.PaidSickLeaveMonthlySalary,
      CalculationRowType.Training,
    ],

    /** Additions to base salary - typically a percentage of base salary */
    salaryAdditions: [
      CalculationRowType.Overtime,
      CalculationRowType.TesWorktimeShortening,
      CalculationRowType.EveningAddition,
      CalculationRowType.NightimeAddition,
      CalculationRowType.SaturdayAddition,
      CalculationRowType.SundayWork,
      CalculationRowType.OtherAdditions,
      CalculationRowType.Remuneration,
      CalculationRowType.OtherCompensation,
      CalculationRowType.WorkingTimeCompensation,
      CalculationRowType.EmploymentTermination,
      CalculationRowType.HourlySalaryWithWorkingTimeCompensation,
    ],

    /** Benefits: Not paid as money but taxable income */
    benefits: [
      CalculationRowType.MealBenefit,
      CalculationRowType.PhoneBenefit,
      CalculationRowType.SubsidisedCommute,
      CalculationRowType.CarBenefit,
      CalculationRowType.BicycleBenefit,
      CalculationRowType.AccomodationBenefit,
      CalculationRowType.OtherBenefit,
      CalculationRowType.ChildCareSubsidy,
    ],

    /** Expenses and other non-taxed compensations */
    expenses: [
      CalculationRowType.DailyAllowance,
      CalculationRowType.Expenses,
      CalculationRowType.MilageOwnCar,
      CalculationRowType.MealCompensation,
      CalculationRowType.ToolCompensation,
      CalculationRowType.MilageDaily,
      CalculationRowType.MilageOther,
      CalculationRowType.NonProfitOrg,
      CalculationRowType.DailyAllowanceHalf,
    ],

    /** Holidays */
    holidays: [
      CalculationRowType.HolidayCompensation,
      CalculationRowType.HolidayBonus,
      CalculationRowType.HolidaySalary,
    ],

    /** Deductions */
    deductions: [
      CalculationRowType.UnionPayment,
      CalculationRowType.Advance,
      CalculationRowType.Foreclosure,
      CalculationRowType.OtherDeductions,
      CalculationRowType.DeductibleOfExerciseAndCultureBenefit,
      CalculationRowType.PrepaidExpenses,
      CalculationRowType.TaxAtSource,
      CalculationRowType.TaxWithholding,
      CalculationRowType.AbsencePeriod,
    ],

    /** Types that are used only as temporary / input types in Calculations - not in the results. */
    salaryCalc: [
      CalculationRowType.TotalWorkerPayment,
      CalculationRowType.TotalEmployerPayment,
      CalculationRowType.Script
    ],

    /** Other row types */
    other: [
      // CalculationRowType.ChildCareSubsidy,
      CalculationRowType.Service,
      CalculationRowType.PayStats
    ],
  };

  private static rowsCache: any = {};

  /** Converts a raw excel generated row configuration to language-versioned JSON object. */
  private static getRowConfigInternal(rowConfig: Partial<CalcRowConfig>): CalcRowConfig | undefined {
    if (!rowConfig) {
      return undefined;
    }
    let kind = rowConfig.kind?.name ? EnumerationsLogic.getEnumMetadata(rowConfig.kind.name) : undefined;
    // TransactionCodes has been translated in incometypelogic, not in enumerationlogic
    if (kind?.name === "TransactionCode") {
      const allTypes = IncomeTypesLogic.getAll();
      for (const value of kind.values) {
        value.label = allTypes.find(x => x.transactionCode === value.name)?.label;
        value.descr = allTypes.find(x => x.transactionCode === value.name)?.description;
      }
    }
    if ((kind?.values?.length ?? 0) < 2) {
      kind = undefined;
    }
    return {
      name: rowConfig!.name!,
      rowType: rowConfig.name as CalculationRowType,
      type: "/builtIn/" + rowConfig.name,
      order: rowConfig.order ?? 0,
      kind: kind,
      label: Translations.get("SALAXY.ENUM.CalculationRowType." + rowConfig.name + ".label"),
      descr: Translations.get("SALAXY.ENUM.CalculationRowType." + rowConfig.name + ".description"),
      moreInfo: Translations.getWithDefault("SALAXY.ENUM.CalculationRowType." + rowConfig.name + ".moreInfo", undefined),
      category: rowConfig.category!,
      favorite: rowConfig.favorite || 0,
      color: rowConfig.color || "#eee",
      iconText: Translations.getWithDefault("SALAXY.ENUM.CalculationRowType." + rowConfig.name + ".iconText", undefined) || "€",
      price: {
        label: Translations.getWithDefault("SALAXY.ENUM.CalculationRowType." + rowConfig.name + ".priceLabel", undefined) || Translations.get("SALAXY.UI_Terms.price"),
        input: rowConfig.price?.input || CalcRowConfigNumberInput.Number,
        default: rowConfig.price?.default || undefined,
      },
      amount: {
        label: Translations.getWithDefault("SALAXY.ENUM.CalculationRowType." + rowConfig.name + ".amountLabel", undefined) || Translations.get("SALAXY.UI_Terms.amount"),
        input: rowConfig?.amount?.input || CalcRowConfigNumberInput.Number,
        default: rowConfig?.amount?.default || undefined,
        unit: rowConfig?.amount?.unit || undefined,
        units: rowConfig?.amount?.units || undefined,
      },
      total: {
        input: rowConfig?.total?.input || CalcRowConfigNumberInput.ReadOnly,
      },
      incomeTypes: rowConfig?.incomeTypes || undefined,
      group: CalcRowConfigTypeGroup.BuiltIn,
    };
  }
}
